import * as React from 'react';
import { withStyles } from '@material-ui/core';
import styles from './linePreview.styles';

const MINIMUM_LINE_THICKNESS = 1;

type PropType = {
  classes: Object,
  zoom: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
}

export const LinePreviewC = ({
  x1,
  y1,
  x2,
  y2,
  classes,
  zoom,
}: PropType) => {
  const defaultStrokeThickness = 3;
  const strokeThicknessWithZoom = defaultStrokeThickness * zoom;

  const adjustedx1 = (x1 || 0) * zoom;

  const adjustedy1 = (y1 || 0) * zoom;

  const adjustedx2 = (x2 || 0) * zoom;

  const adjustedy2 = (y2 || 0) * zoom;

  // Trig Time
  // Line is rendered with a div, and a div is rendered with a top, left absolute positioning
  // and then rotated by degrees on its midpoint.
  // Therefore, need to translate x1, y1, x2, y2 coordinates into top, left and rotation degree to render the div correctly.
  const calculatedLineLength = Math.sqrt(((adjustedx1 - adjustedx2) ** 2) + ((adjustedy1 - adjustedy2) ** 2));
  const midPoint = {
    x: (adjustedx1 + adjustedx2) / 2,
    y: (adjustedy1 + adjustedy2) / 2,
  };
  const opposite = Math.abs(adjustedy1 - adjustedy2) / 2;
  const hypoteneuse = calculatedLineLength / 2;
  const oppositeOverHypoteneuse = opposite / hypoteneuse;
  const radians = Math.asin(oppositeOverHypoteneuse);
  let angle = radians * (180 / Math.PI);

  // isUpperPointTopRight is true if the vertex closest to the top of the page is further right
  // than the second vertex of the line.
  // The angle calculation above is found simply by using the absolute value of (y1 - y2) then divided by 2 to find
  // the length of the opposite side of a right triangle in which the hypoteneuse = calculatedLineLength / 2,
  // so this is basically assuming the line runs diagonally from the top left to the bottom right.
  // If isUpperPointTopRight is true, that means the line runs top right to bottom left,
  // and the angle calculation needs to be 'reversed' by finding the difference between the
  // calculated angle and 180.
  const uppermostPoint = adjustedy1 < adjustedy2 ? 'adjustedy1' : 'adjustedy2';
  const isUpperPointTopRight = (uppermostPoint === 'adjustedy1' && adjustedx1 > adjustedx2)
      || (uppermostPoint === 'adjustedy2' && adjustedx2 > adjustedx1);

  if (isUpperPointTopRight) {
    angle = Math.abs(angle - 180);
  }

  const topLeftCorner = {
    x: midPoint.x - (calculatedLineLength / 2),
    y: midPoint.y,
  };

  return (
    <div
      role="button"
      tabIndex="-1"
      data-testid="line-field-container"
      style={{
        height: 0,
        width: calculatedLineLength,
        transform: `translate(${topLeftCorner.x}px, ${topLeftCorner.y}px) rotate(${angle}deg)`,
      }}
      className={classes.fieldContainer}
    >
      <div
        style={{
          height: strokeThicknessWithZoom < MINIMUM_LINE_THICKNESS ? MINIMUM_LINE_THICKNESS : strokeThicknessWithZoom,
          width: calculatedLineLength,
          borderRadius: 5 * zoom,
        }}
        className={classes.field}
      />
    </div>
  );

};

export default withStyles(styles, { withTheme: true })(LinePreviewC);
